import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CEDatePicker from "components/CEDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import dayjs from "dayjs";

const PAGE_SIZE = 500;

const PRESETS = [
  { key: "1h", label: "Last hour" },
  { key: "6h", label: "Last 6 hours" },
  { key: "today", label: "Today" },
  { key: "24h", label: "Last 24h" },
  { key: "7d", label: "Last 7 days" },
  { key: "custom", label: "Custom" },
];

/**
 * Logs viewer content (inline — no overlay wrapper).
 *
 * Rendered directly by the Logs page (`/logs`, admin only). Same UI and
 * behavior as the old Logs Viewer overlay: the overlay shell (back button,
 * header, drag/maximize persistence) was the only thing dropped when the Bug
 * Reports screen was removed (2026-10-05).
 *
 * Reads server log files and entries via the admin log endpoints:
 *   GET /logs/files    → [{ name, size, modifiedAt, lines? }]
 *   GET /logs/entries  → { total, counts, entries }
 */
const LogsViewerContent = () => {
  const theme = useTheme();
  const { showSnackbar } = useStateContext();

  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState("server.log");
  const [period, setPeriod] = useState("24h");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [level, setLevel] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // User attribution filter — "all" or a numeric member id. The list is
  // returned by /logs/entries (users present in the current time window).
  const [userFilter, setUserFilter] = useState("all");
  const [users, setUsers] = useState([]);

  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ info: 0, warn: 0, error: 0, crash: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // When a level is selected, also fetch the request's OTHER lines (OK/WARN
  // trail) and show them next to the matching entries. Server-side grouping
  // by the [req:...] correlation id (2026-09-22).
  const [showRelated, setShowRelated] = useState(true);
  // Offset for "Load more" counts MATCHED entries only — related lines ride
  // along with their page and must not advance the server-side slice.
  const matchedLoadedRef = useRef(0);

  // Load the available log files once on mount.
  useEffect(() => {
    const loadFiles = async () => {
      try {
        const response = await api.get("/logs/files");
        const list = Array.isArray(response.data) ? response.data : [];
        setFiles(list);
        if (
          list.length > 0 &&
          !list.some((file) => file.name === selectedFile)
        ) {
          setSelectedFile(list[0].name);
        }
      } catch (err) {
        showSnackbar(
          err?.response?.data?.error || "Unable to load log files",
          theme,
          "error",
        );
      }
    };
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search box (300ms) before it hits the API.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const selectedFileInfo = useMemo(
    () => files.find((file) => file.name === selectedFile) || null,
    [files, selectedFile],
  );

  const getRange = useCallback(() => {
    const now = dayjs();
    switch (period) {
      case "1h":
        return { from: now.subtract(1, "hour"), to: now };
      case "6h":
        return { from: now.subtract(6, "hour"), to: now };
      case "today":
        return { from: now.startOf("day"), to: now };
      case "7d":
        return { from: now.subtract(7, "day"), to: now };
      case "custom":
        return { from: customFrom, to: customTo };
      default:
        return { from: now.subtract(24, "hour"), to: now };
    }
  }, [period, customFrom, customTo]);

  const fetchEntries = useCallback(
    async (offset, append) => {
      const { from, to } = getRange();
      const params = { file: selectedFile, limit: PAGE_SIZE, offset };
      if (from) params.from = from.toISOString();
      if (to) params.to = to.toISOString();
      if (level !== "all") params.level = level;
      if (userFilter !== "all") params.user = userFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (showRelated && level !== "all") params.includeContext = "true";

      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const response = await api.get("/logs/entries", { params });
        const data = response.data || {};
        const newEntries = Array.isArray(data.entries) ? data.entries : [];
        setEntries((prev) => (append ? [...prev, ...newEntries] : newEntries));
        setCounts(data.counts || { info: 0, warn: 0, error: 0, crash: 0 });
        // User list is computed over the time window only — safe to refresh
        // on every fetch without the dropdown churning as filters change.
        setUsers(Array.isArray(data.users) ? data.users : []);
        const newTotal = data.total ?? offset + newEntries.length;
        setTotal(newTotal);
        const matchedInPage = newEntries.filter((entry) => !entry.isContext).length;
        matchedLoadedRef.current = (append ? matchedLoadedRef.current : 0) + matchedInPage;
        setHasMore(matchedLoadedRef.current < newTotal);
      } catch (err) {
        showSnackbar(
          err?.response?.data?.error || "Unable to load log entries",
          theme,
          "error",
        );
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [getRange, selectedFile, level, userFilter, debouncedSearch, showRelated, showSnackbar, theme],
  );

  // Fetch whenever the file, time range, level, or (debounced) search changes.
  useEffect(() => {
    fetchEntries(0, false);
  }, [fetchEntries]);

  // Related-line counts — context entries are non-matching lines that belong
  // to a shown request (same [req:...] id), so they are excluded from the
  // matched tally shown to the user.
  const relatedCount = useMemo(
    () => entries.filter((entry) => entry.isContext).length,
    [entries],
  );
  const matchedCount = entries.length - relatedCount;

  const formatSize = (bytes) => {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimestamp = (value) =>
    value ? dayjs.tz(value).format("M/D/YYYY h:mm:ss A") : "";

  const levelColor = (entryLevel) => {
    switch (entryLevel) {
      case "warn":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      case "crash":
        return theme.palette.error.dark;
      default:
        return theme.palette.primary.main;
    }
  };

  const LEVEL_FILTERS = [
    { key: "all", label: "All", color: theme.palette.primary.text },
    { key: "info", label: "Info", color: theme.palette.primary.main },
    { key: "warn", label: "Warn", color: theme.palette.warning.main },
    { key: "error", label: "Error", color: theme.palette.error.main },
    { key: "crash", label: "Crash", color: theme.palette.error.dark },
  ];

  // Visible field borders — the default outlined border is too faint against
  // the card background, so give the fields a clear border + hover/focus states.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.default,
      borderRadius: "10px",
      "& fieldset": {
        borderColor: theme.palette.primary.border2,
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  };

  // The Select root IS the OutlinedInput (no inner wrapper), so the shared
  // fieldSx descendant selector never matches it — style the root directly.
  // This also overrides the theme's MuiSelect tint (colorsLight.main[50] in
  // light mode) that paints the Select interior blue-ish.
  const selectFieldSx = {
    backgroundColor: theme.palette.background.default,
    borderRadius: "10px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.border2,
      borderWidth: 1,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  };

  // Line count helper — older backends may not return `lines` yet, so
  // degrade gracefully to no count.
  const formatLineCount = (lines) => {
    if (typeof lines !== "number" || !Number.isFinite(lines)) return null;
    return `${lines.toLocaleString()} lines`;
  };

  return (
    <Box sx={{ width: "100%", pb: 1.5 }}>
      {/* Filter panel — file + period on top, search + user below */}
      <Box
        sx={{
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "12px",
          p: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Select
            size="small"
            value={selectedFile}
            onChange={(event) => setSelectedFile(event.target.value)}
            sx={{ minWidth: 220, ...selectFieldSx }}
          >
            {files.map((file) => {
              const lineCount = formatLineCount(file.lines);
              return (
                <MenuItem key={file.name} value={file.name}>
                  {file.name}
                  {lineCount ? ` (${lineCount})` : ""}
                </MenuItem>
              );
            })}
          </Select>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {PRESETS.map((preset) => (
              <Button
                key={preset.key}
                size="small"
                onClick={() => setPeriod(preset.key)}
                sx={{
                  color:
                    period === preset.key
                      ? theme.palette.background.default
                      : theme.palette.primary.main,
                  backgroundColor:
                    period === preset.key
                      ? theme.palette.primary.main
                      : "transparent",
                  border: `1px solid ${theme.palette.primary.main}`,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor:
                      period === preset.key
                        ? theme.palette.primary.main
                        : theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            mt: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ flex: "1 1 200px", minWidth: 180, ...fieldSx }}
          />

          {/* User attribution filter — populated from the entries response. */}
          <Select
            size="small"
            displayEmpty
            value={userFilter}
            onChange={(event) => setUserFilter(event.target.value)}
            sx={{ minWidth: 190, ...selectFieldSx }}
            renderValue={(value) => {
              if (value === "all") return "All users";
              const found = users.find(
                (u) => String(u.id) === String(value),
              );
              return found
                ? `${found.name || `User #${found.id}`} (${found.count})`
                : `User #${value}`;
            }}
          >
            <MenuItem value="all">All users</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name || `User #${user.id}`} ({user.count})
              </MenuItem>
            ))}
          </Select>
        </Box>

        {period === "custom" && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
            <CEDatePicker
              label="From"
              value={customFrom}
              onChange={(newValue) => setCustomFrom(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  InputLabelProps: { shrink: true },
                  sx: { minWidth: 160, ...fieldSx },
                },
              }}
            />
            <CEDatePicker
              label="To"
              value={customTo}
              onChange={(newValue) => setCustomTo(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  InputLabelProps: { shrink: true },
                  sx: { minWidth: 160, ...fieldSx },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Summary strip — file metadata on the left, result count on the right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        {selectedFileInfo ? (
          <Typography variant="caption" color="text.secondary">
            {formatSize(selectedFileInfo.size)} · Modified{" "}
            {formatTimestamp(selectedFileInfo.modifiedAt)}
            {formatLineCount(selectedFileInfo.lines)
              ? ` · ${formatLineCount(selectedFileInfo.lines)}`
              : ""}
          </Typography>
        ) : (
          <span />
        )}
        <Typography variant="caption" color="text.secondary">
          {total === 0
            ? "No entries"
            : `Showing ${matchedCount.toLocaleString()} of ${total.toLocaleString()} entr${total === 1 ? "y" : "ies"}${relatedCount > 0 ? ` · +${relatedCount.toLocaleString()} related line${relatedCount === 1 ? "" : "s"}` : ""}`}
        </Typography>
      </Box>

      {/* Level filter chips with counts */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        {LEVEL_FILTERS.map(({ key, label, color }) => {
          // "All" = sum of the per-level counts. The server computes those
          // with the level filter excluded, so the chips stay informative
          // while a specific level is selected (previously Info/Warn read
          // (0) under an Error filter, looking like they did not exist).
          // `total` remains the fully filtered count used for paging.
          const count =
            key === "all"
              ? (counts.info ?? 0) +
                (counts.warn ?? 0) +
                (counts.error ?? 0) +
                (counts.crash ?? 0)
              : counts[key] ?? 0;
          const selected = level === key;
          return (
            <Chip
              key={key}
              size="small"
              label={`${label} (${count})`}
              onClick={() => setLevel(key)}
              sx={{
                color: selected ? theme.palette.background.default : color,
                backgroundColor: selected ? color : "transparent",
                border: `1px solid ${color}`,
                fontWeight: selected ? "bold" : "normal",
                "&:hover": {
                  backgroundColor: selected ? color : `${color}20`,
                },
              }}
            />
          );
        })}
        {level !== "all" && (
          <FormControlLabel
            sx={{ ml: "auto", mr: 0 }}
            control={
              <Switch
                size="small"
                checked={showRelated}
                onChange={(event) => setShowRelated(event.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Show related lines
              </Typography>
            }
          />
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Entries */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            border: `1px dashed ${theme.palette.primary.border2}`,
            borderRadius: "12px",
          }}
        >
          <Typography color="text.secondary">
            No log entries in this range
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            Try a wider time period or clear the filters
          </Typography>
        </Box>
      ) : (
        <>
          {entries.map((entry, index) => (
            <Box
              key={`${entry.timestamp}-${index}`}
              sx={{
                bgcolor: theme.palette.background.default,
                border: `1px solid ${theme.palette.primary.border2}`,
                // Left accent bar colored by level — scans much faster
                // than reading the chip on every row.
                borderLeft: `4px solid ${levelColor(entry.level)}`,
                borderRadius: "10px",
                p: 1.25,
                mb: 1,
                // Related (context) lines are dimmed so they read as the
                // trail around the matching entry, not as results.
                opacity: entry.isContext ? 0.82 : 1,
                transition: "border-color 0.15s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  borderLeftColor: levelColor(entry.level),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  size="small"
                  label={entry.level}
                  sx={{
                    color: theme.palette.background.default,
                    backgroundColor: levelColor(entry.level),
                    fontWeight: "bold",
                    fontSize: "0.65rem",
                    height: 20,
                  }}
                />
                {entry.isContext && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Related"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      borderColor: theme.palette.primary.border2,
                      color: theme.palette.text.secondary,
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {formatTimestamp(entry.timestamp)}
                </Typography>
                {entry.userIds?.length > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      entry.userIds.length > 1
                        ? (entry.userNames || []).join(", ")
                        : entry.userName || `User #${entry.userIds[0]}`
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      borderColor: theme.palette.primary.border2,
                      color: theme.palette.primary.text,
                    }}
                  />
                )}
                {entry.line != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    Line {entry.line}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  bgcolor: theme.palette.background.alt,
                  borderRadius: "8px",
                  px: 1.25,
                  py: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "0.78rem",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: theme.palette.primary.text,
                  }}
                >
                  {entry.message}
                </Typography>
              </Box>
            </Box>
          ))}

          {hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5, mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={loadingMore}
                onClick={() => fetchEntries(matchedLoadedRef.current, true)}
                sx={{
                  minWidth: 200,
                  borderRadius: "12px",
                  py: 1,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  fontWeight: "bold",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.primary.border2,
                    color: theme.palette.background.default,
                  },
                }}
              >
                {loadingMore ? (
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1, color: theme.palette.background.default }}
                  />
                ) : (
                  "Load more"
                )}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default LogsViewerContent;
